// Product search hook with Algolia + Firestore fallback
import { useState, useEffect, useCallback, useRef } from 'react';
import { searchClient, ALGOLIA_INDEX_NAME, isAlgoliaConfigured } from '../config/algolia';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface SearchProduct {
  id: string;
  objectID?: string;
  name: string;
  brand: string;
  brandId?: string;
  description?: string;
  price: number;
  compareAtPrice?: number | null;
  saleSchedule?: {
    startDate: string;
    endDate: string;
  } | null;
  images?: string[];
  imageUrl?: string;
  sizes?: Array<{ label: string; available: boolean }>;
  category?: string;
  tags?: string[];
  sale?: boolean;
}

export interface SearchFilters {
  category?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  /** When true, only return products explicitly marked as sale. */
  sale?: boolean;
}

export interface SearchResult {
  hits: SearchProduct[];
  totalHits: number;
  query: string;
  facets?: {
    categories: Array<{ value: string; count: number }>;
    brands: Array<{ value: string; count: number }>;
  };
  processingTimeMs: number;
}

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ─── Algolia Search ─────────────────────────────────────────────────
async function searchAlgolia(
  searchQuery: string,
  filters: SearchFilters = {},
  page: number = 0,
  hitsPerPage: number = 20
): Promise<SearchResult> {
  if (!searchClient) {
    throw new Error('Algolia client not initialized');
  }

  const startTime = performance.now();

  // Build filter string
  const filterParts: string[] = ['active:true'];
  if (filters.category && filters.category !== 'All') {
    filterParts.push(`category:"${filters.category}"`);
  }
  if (filters.brand) {
    filterParts.push(`brand:"${filters.brand}"`);
  }
  if (filters.sale === true) {
    filterParts.push('sale:true');
  }
  if (filters.minPrice !== undefined) {
    filterParts.push(`price >= ${filters.minPrice}`);
  }
  if (filters.maxPrice !== undefined) {
    filterParts.push(`price <= ${filters.maxPrice}`);
  }

  const results = await searchClient.searchSingleIndex({
    indexName: ALGOLIA_INDEX_NAME,
    searchParams: {
      query: searchQuery,
      filters: filterParts.join(' AND '),
      page,
      hitsPerPage,
      attributesToRetrieve: [
        'name', 'brand', 'brandId', 'description', 'price',
        'compareAtPrice', 'saleSchedule',
        'images', 'category', 'tags', 'sizes', 'sale'
      ],
      facets: ['category', 'brand'],
    },
  });

  const processingTimeMs = performance.now() - startTime;

  // Map Algolia hits to our product format
  const hits: SearchProduct[] = (results.hits || []).map((hit: any) => ({
    id: hit.objectID,
    objectID: hit.objectID,
    name: hit.name || '',
    brand: hit.brand || '',
    brandId: hit.brandId || '',
    description: hit.description || '',
    price: hit.price || 0,
    compareAtPrice: hit.compareAtPrice || null,
    saleSchedule: hit.saleSchedule || null,
    images: hit.images || [],
    imageUrl: hit.images?.[0] || '',
    sizes: hit.sizes || [],
    category: hit.category || '',
    tags: hit.tags || [],
    sale: hit.sale === true,
  }));

  // Map facets
  const facets = {
    categories: Object.entries(results.facets?.category || {}).map(([value, count]) => ({
      value,
      count: count as number,
    })),
    brands: Object.entries(results.facets?.brand || {}).map(([value, count]) => ({
      value,
      count: count as number,
    })),
  };

  return {
    hits,
    totalHits: results.nbHits || 0,
    query: searchQuery,
    facets,
    processingTimeMs,
  };
}

// ─── Firestore Fallback Search ──────────────────────────────────────
async function searchFirestore(
  searchQuery: string,
  filters: SearchFilters = {},
  maxResults: number = 20
): Promise<SearchResult> {
  const startTime = performance.now();
  const searchLower = searchQuery.toLowerCase().trim();

  let products: any[] = [];

  // Try the indexed query first (active == true + ordering).
  // If it fails (missing index, or products lack the 'active' field),
  // fall back to a simple unfiltered fetch so search still works.
  try {
    let q;
    if (filters.category && filters.category !== 'All') {
      q = query(
        collection(db, 'products'),
        where('active', '==', true),
        where('category', '==', filters.category),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
    } else {
      q = query(
        collection(db, 'products'),
        where('active', '==', true),
        orderBy('createdAt', 'desc'),
        limit(100)
      );
    }
    const snapshot = await getDocs(q);
    products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (indexError) {
    console.warn('Indexed product query failed, falling back to full scan:', indexError);
  }

  // If the indexed query returned nothing, try without the active filter.
  // Products may not have the 'active' field set at all.
  if (products.length === 0) {
    try {
      const fallbackQ = query(
        collection(db, 'products'),
        limit(200)
      );
      const fallbackSnap = await getDocs(fallbackQ);
      products = fallbackSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Exclude explicitly deactivated products (active === false) but keep
      // products where the field is missing (undefined/null) since those are
      // likely just not tagged yet.
      products = products.filter(p => p.active !== false);
    } catch (fallbackError) {
      console.error('Firestore product query failed entirely:', fallbackError);
      throw fallbackError;
    }
  }

  // Client-side text filtering
  if (searchLower) {
    products = products.filter(p => {
      const nameMatch = p.name?.toLowerCase().includes(searchLower);
      const brandMatch = p.brand?.toLowerCase().includes(searchLower);
      const descMatch = p.description?.toLowerCase().includes(searchLower);
      const tagMatch = p.tags?.some((t: string) => t.toLowerCase().includes(searchLower));
      const categoryMatch = p.category?.toLowerCase().includes(searchLower);
      return nameMatch || brandMatch || descMatch || tagMatch || categoryMatch;
    });
  }

  // Client-side category filter (needed when the fallback query was used)
  if (filters.category && filters.category !== 'All') {
    products = products.filter(p => p.category === filters.category);
  }
  if (filters.brand) {
    products = products.filter(p => p.brand === filters.brand);
  }
  if (filters.sale === true) {
    products = products.filter(p => p.sale === true);
  }
  if (filters.minPrice !== undefined) {
    products = products.filter(p => p.price >= filters.minPrice!);
  }
  if (filters.maxPrice !== undefined) {
    products = products.filter(p => p.price <= filters.maxPrice!);
  }

  // Build facets from results
  const categoryMap = new Map<string, number>();
  const brandMap = new Map<string, number>();
  products.forEach(p => {
    if (p.category) categoryMap.set(p.category, (categoryMap.get(p.category) || 0) + 1);
    if (p.brand) brandMap.set(p.brand, (brandMap.get(p.brand) || 0) + 1);
  });

  const processingTimeMs = performance.now() - startTime;

  const hits: SearchProduct[] = products.slice(0, maxResults).map(p => ({
    id: p.id,
    name: p.name || '',
    brand: p.brand || '',
    brandId: p.brandId || '',
    description: p.description || '',
    price: p.price || 0,
    compareAtPrice: p.compareAtPrice || null,
    saleSchedule: p.saleSchedule || null,
    images: p.images || [],
    imageUrl: p.images?.[0] || '',
    sizes: p.sizes || [],
    category: p.category || '',
    tags: p.tags || [],
    sale: p.sale === true,
  }));

  return {
    hits,
    totalHits: products.length,
    query: searchQuery,
    facets: {
      categories: Array.from(categoryMap.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count),
      brands: Array.from(brandMap.entries())
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count),
    },
    processingTimeMs,
  };
}

// ─── Main Search Hook ───────────────────────────────────────────────
export function useSearch(options?: {
  debounceMs?: number;
  hitsPerPage?: number;
  filters?: SearchFilters;
}) {
  const { debounceMs = 300, hitsPerPage = 20, filters = {} } = options || {};
  
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const debouncedQuery = useDebounce(searchQuery, debounceMs);
  const abortRef = useRef<AbortController | null>(null);

  const executeSearch = useCallback(async (query: string, searchFilters: SearchFilters) => {
    const hasBrowseFilters = Boolean(
      (searchFilters.category && searchFilters.category !== 'All') || searchFilters.sale === true
    );
    if (!query.trim() && !hasBrowseFilters) {
      setResults(null);
      setLoading(false);
      return;
    }

    // Cancel previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      let result: SearchResult;
      
      if (isAlgoliaConfigured) {
        result = await searchAlgolia(query, searchFilters, 0, hitsPerPage);
      } else {
        console.log(`[Search] Using Firestore fallback for query: "${query}"`);
        result = await searchFirestore(query, searchFilters, hitsPerPage);
        console.log(`[Search] Firestore returned ${result.totalHits} results in ${result.processingTimeMs.toFixed(0)}ms`);
      }

      setResults(result);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
        const message = err?.code === 'failed-precondition'
          ? 'Search index not ready. Please try again shortly.'
          : err.message || 'Search failed. Please try again.';
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [hitsPerPage]);

  // Trigger search when debounced query or filters change
  useEffect(() => {
    executeSearch(debouncedQuery, filters);
  }, [debouncedQuery, filters, executeSearch]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setResults(null);
    setError(null);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    results,
    loading,
    error,
    clearSearch,
    isAlgoliaEnabled: isAlgoliaConfigured,
  };
}

// ─── Standalone search function (for one-off searches) ──────────────
export async function performSearch(
  query: string,
  filters: SearchFilters = {},
  hitsPerPage: number = 20
): Promise<SearchResult> {
  if (isAlgoliaConfigured) {
    return searchAlgolia(query, filters, 0, hitsPerPage);
  }
  return searchFirestore(query, filters, hitsPerPage);
}

// ─── Popular/Recent searches ────────────────────────────────────────
const RECENT_SEARCHES_KEY = 'fash_recent_searches';
const MAX_RECENT_SEARCHES = 8;

export function getRecentSearches(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function addRecentSearch(query: string) {
  try {
    const trimmed = query.trim();
    if (!trimmed) return;
    
    const recent = getRecentSearches().filter(s => s !== trimmed);
    recent.unshift(trimmed);
    localStorage.setItem(
      RECENT_SEARCHES_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT_SEARCHES))
    );
  } catch {
    // Ignore storage errors
  }
}

export function clearRecentSearches() {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    // Ignore storage errors
  }
}
